import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Container from "components/__reusable/Container";
import LoadingDialog from "components/__reusable/LoadingDialog";
import PictureThumbnail from "components/__reusable/PictureThumbnail";
import { getPublishedPictureSummarizationByClass } from "services/summarizationServices";
import { getPublishedPictures } from "services/pictureServices";
import type { SummarizationByClass } from "types/Summarization";
import type { Picture } from "types/Pictures";
import { formatDate } from "utilities/DateFormatter";
import { SupportedLang } from "config/constants";
import { PICTURES_RESULTS_PATH } from "router/pathName";

type Props = {
  startDate: string;
  endDate: string;
};
const Summarization: React.FC<Props> = (props: Props) => {
  const { startDate, endDate } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [summaries, setSummaries] = useState<{
    [key: string]: SummarizationByClass[];
  }>({});
  const [picturesByClass, setPicturesByClass] = useState<{
    [classId: string]: Picture[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchSummaries = useCallback(async () => {
    try {
      setIsLoading(true);
      setSummaries({});
      setPicturesByClass({}); // Updated state setter

      const summariesResponse = await getPublishedPictureSummarizationByClass(
        startDate,
        endDate,
      );
      const summariesMap: { [key: string]: SummarizationByClass[] } = {};
      for (let summary of summariesResponse) {
        if (summariesMap[summary.date])
          summariesMap[summary.date].push(summary);
        else summariesMap[summary.date] = [summary];
      }
      setSummaries(summariesMap);

      const publishedPictures = await getPublishedPictures(
        1,
        startDate,
        endDate,
      );

      const picturesGroupedByClass: { [classId: string]: Picture[] } = {};
      const force = publishedPictures as unknown as Picture[];

      for (const picture of force) {
        const classId = picture.class_id.toString();
        if (!picturesGroupedByClass[classId]) {
          picturesGroupedByClass[classId] = [];
        }
        picturesGroupedByClass[classId].push(picture);
      }

      setPicturesByClass(picturesGroupedByClass); // Updated state setter
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchSummaries();
  }, [startDate, endDate, fetchSummaries]);

  const viewPictures = (date: string, classId: number) => {
    const params = new URLSearchParams({
      startDate: date,
      endDate: date,
      classId: classId.toString(),
      date,
      source: "publishedDate",
    });

    navigate(`${PICTURES_RESULTS_PATH}?${params.toString()}`);
  };

  return (
    <div className="h-full w-full flex flex-col space-y-1 lg:space-y-3">
      <div className="flex-1">
        <Container>
          <div className="h-full overflow-auto relative">
            <div className="w-full h-full absolute">
              <div className="h-[95%] overflow-y-auto space-y-3 lg:space-y-5">
                {isLoading && <LoadingDialog />}
                {Object.keys(summaries).length === 0 && (
                  <p>{t("_generic._no_data")}</p>
                )}
                {Object.keys(summaries).map((date) => (
                  <div key={date} className="space-y-1 lg:space-y-2">
                    <h3 className="text-xl font-semibold">
                      {formatDate(new Date(date), {
                        [SupportedLang.ja]: "Mo do (EEE)",
                        [SupportedLang.en]: "EEEE, MMM do",
                      })}
                    </h3>
                    {summaries[date].map((summary) => (
                      <div key={summary.class_id} className="space-y-2">
                        <div className="flex space-x-2 text-lg">
                          <p>{summary.class_name}: </p>
                          <button
                            className="underline hover:no-underline text-[#7093cf]"
                            onClick={() =>
                              viewPictures(summary.date, summary.class_id)
                            }
                          >
                            {summary.number_published_picture}{" "}
                            {t("_published_picture._images")}
                          </button>
                        </div>

                        {picturesByClass[summary.class_id.toString()] && (
                          <div className="flex flex-wrap gap-[1px]">
                            {picturesByClass[summary.class_id.toString()]
                              .slice(0, 10)
                              .map((picture) => (
                                <button
                                  key={picture.id}
                                  type="button"
                                  className="w-12 h-12 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                  onClick={() =>
                                    viewPictures(
                                      summary.date,
                                      summary.class_id,
                                    )
                                  }
                                  aria-label={`picture-${picture.id}`}
                                >
                                  <PictureThumbnail
                                    imageSrc={picture.thumbnail_picture}
                                    name={`picture-${picture.id}`}
                                    rounded={false}
                                  />
                                </button>
                              ))}
                            {picturesByClass[summary.class_id.toString()]
                              .length > 10 && (
                              <div className="w-12 h-12 flex items-center justify-center bg-gray-200 text-gray-600 text-sm rounded">
                                +
                                {picturesByClass[summary.class_id.toString()]
                                  .length - 10}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Summarization;
